import { useCallback } from 'react';
import { useConnectionsList } from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import type {
	RemoveConnectionCallback,
	StatusUpdateHandler,
} from '@atlassian/jira-issue-table-hide-done-toggle/src/controllers/handle-status-update/types.tsx';
import { useHandleStatusUpdate } from '@atlassian/jira-issue-table-hide-done-toggle/src/controllers/handle-status-update/index.tsx';
import { useIsHideDoneItemsEnabled } from '../features/selectors.tsx';
import { useUpdateConnectionDetails } from '../issue-realtime-updater/utils.tsx';

// For status update with hide done it will always be a single work item that is removed
const NET_CHANGE_ISSUE_COUNT = -1;

export function useHideDoneItems({
	groupConnectionId,
	itemsConnectionId,
	recordId,
	projectKey,
}: {
	groupConnectionId?: string;
	itemsConnectionId: string;
	recordId: string;
	projectKey: string;
}): StatusUpdateHandler {
	const isHideDoneEnabled = useIsHideDoneItemsEnabled();

	const { connections } = useConnectionsList();

	const getConnectionDetails = useCallback(
		() =>
			Object.values(connections).find((connection) => {
				return connection.connectionId === itemsConnectionId;
			}),
		[connections, itemsConnectionId],
	);

	const updateConnectionDetails = useUpdateConnectionDetails();

	const removeConnection = useCallback<RemoveConnectionCallback>(
		({ store, remainingIssueCount }) => {
			const connectionDetails = getConnectionDetails();
			if (!connectionDetails) return;

			updateConnectionDetails({
				store,
				connection: connectionDetails,
				remainingIssueCount,
				netChangeInIssueCount: NET_CHANGE_ISSUE_COUNT,
				groupConnectionId,
				projectKey,
			});
		},
		[getConnectionDetails, groupConnectionId, projectKey, updateConnectionDetails],
	);

	const handler = useHandleStatusUpdate(itemsConnectionId, recordId, removeConnection, {
		isHideDoneEnabled,
	});

	return handler;
}
